import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { cx } from './primitives'

type ToastTone = 'info' | 'success' | 'error'

interface Toast {
  id: number
  tone: ToastTone
  message: string
}

interface ToastState {
  show: (message: string, tone?: ToastTone) => void
}

const ToastContext = createContext<ToastState | undefined>(undefined)

const TONES: Record<ToastTone, string> = {
  info: 'border-line bg-surface text-ink',
  success: 'border-pos/30 bg-pos-wash text-pos',
  error: 'border-crit/30 bg-crit-wash text-crit',
}

let nextId = 1

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])

  const show = useCallback((message: string, tone: ToastTone = 'info') => {
    const id = nextId++
    setToasts((current) => [...current, { id, tone, message }])

    // Errors stay longer: they usually carry something the reader has to act on.
    window.setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id))
    }, tone === 'error' ? 8000 : 4000)
  }, [])

  const value = useMemo(() => ({ show }), [show])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        // Announced rather than only shown: a confirmation nobody can hear is not a
        // confirmation.
        role="status"
        aria-live="polite"
        className="pointer-events-none fixed bottom-5 right-5 z-50 flex w-[min(92vw,380px)] flex-col gap-2"
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={cx(
              'pointer-events-auto rounded-md border px-4 py-3 text-sm shadow-float',
              TONES[toast.tone],
            )}
          >
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastState {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToast must be used inside ToastProvider')

  return context
}
